/**
 * Left-side nav for the authenticated chat view.
 *
 * Hosts:
 *   - profile chip (self displayName/username + inline editor)
 *   - user search (debounced; results → invite the inviteTarget on click)
 *   - "new conversation" + "find opponent" actions
 *   - the conversation list (named, with member count)
 *
 * Talks to the /users/* worker routes via ChatClient. Emits selection
 * events to the parent Chat.
 */
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import type { ConversationId, UserId } from '@chat/protocol';
import type { ChatClient } from '../client/api.js';
import type { BackupBundle } from '../client/backup.js';
import type { ConversationManager } from '../client/conversation.js';
import type { Identity } from '../client/identity.js';
import { type MemberDirectory, shortId } from '../client/member-directory.js';
import { useUserSearch } from '../client/use-user-search.js';
import { initialsFor } from './Chat.js';

interface Profile {
  userId: string;
  username?: string;
  displayName?: string;
  avatarMediaId?: string;
}

/** Avatar uploads are capped well below the generic media limit so a
 *  profile picture never eats the user's 10MB / message budget. */
const AVATAR_MAX_BYTES = 512 * 1024;

export interface SidebarProps {
  readonly client: ChatClient;
  readonly token: string;
  readonly directory: MemberDirectory;
  readonly identity: Identity;
  readonly conversations: readonly ConversationId[];
  readonly selected: ConversationId | undefined;
  readonly onSelect: (id: ConversationId) => void;
  readonly onCreate: () => void;
  readonly onFindOpponent: () => void;
  readonly onEnableNotifications: () => void;
  readonly matchmakingStatus: 'idle' | 'searching' | 'waiting';
  readonly pushStatus: 'idle' | 'granted' | 'denied' | 'unsupported';
  readonly manager: ConversationManager | undefined;
  readonly onDeleteConversation: (id: ConversationId) => void;
  readonly onExportBackup?: () => Promise<BackupBundle>;
  /**
   * Optional jump-to-message handler. When the user clicks a message-
   * search hit, the sidebar dispatches both the conversation id AND
   * the matched sequence so the parent can scroll the chat to that
   * specific bubble.
   */
  readonly onSelectMessage?: (id: ConversationId, sequence: number) => void;
}

export function Sidebar({
  client,
  token,
  directory,
  identity,
  conversations,
  selected,
  onSelect,
  onCreate,
  onFindOpponent,
  onEnableNotifications,
  matchmakingStatus,
  pushStatus,
  manager,
  onDeleteConversation,
  onExportBackup,
  onSelectMessage,
}: SidebarProps) {
  const [profile, setProfile] = useState<Profile | undefined>();
  const [profileError, setProfileError] = useState<string | undefined>();
  const [profileStatus, setProfileStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [editingProfile, setEditingProfile] = useState(false);
  const [usernameDraft, setUsernameDraft] = useState('');
  const [displayNameDraft, setDisplayNameDraft] = useState('');

  const {
    query: searchQuery,
    setQuery: setSearchQuery,
    results,
    error: searchError,
    reset: resetSearch,
  } = useUserSearch(client, token);
  /** Error specifically from the invite-on-click path (vs. the search itself). */
  const [inviteError, setInviteError] = useState<string | undefined>();
  const [msgQuery, setMsgQuery] = useState('');
  const [msgResults, setMsgResults] = useState<
    {
      conversationId: ConversationId;
      sequence: number;
      at: number;
      direction: 'sent' | 'received';
      senderUserId?: UserId;
      snippet: string;
    }[]
  >([]);
  const [msgSearching, setMsgSearching] = useState(false);

  // Debounced message search: whenever msgQuery changes, wait 200ms
  // then ask the manager to grep its local plaintext store. No
  // network, no decryption; everything is already in the sealed
  // keystore.
  useEffect(() => {
    if (!manager) {
      setMsgResults([]);
      return;
    }
    const needle = msgQuery.trim();
    if (needle.length < 2) {
      setMsgResults([]);
      setMsgSearching(false);
      return;
    }
    let cancelled = false;
    setMsgSearching(true);
    const handle = setTimeout(() => {
      void (async () => {
        try {
          const hits = await manager.searchMessages(needle);
          if (!cancelled) setMsgResults(hits);
        } finally {
          if (!cancelled) setMsgSearching(false);
        }
      })();
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [manager, msgQuery]);

  // Load own profile from the server so we can show current values.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const p = await client.getMyProfile(token);
        if (cancelled) return;
        setProfile(p);
        setUsernameDraft(p.username ?? '');
        setDisplayNameDraft(p.displayName ?? '');
        directory.seed(p);
      } catch (err) {
        if (!cancelled) {
          setProfileError(err instanceof Error ? err.message : 'profile load failed');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, token, directory]);

  async function handleAvatarFile(file: File): Promise<void> {
    setProfileError(undefined);
    if (!file.type.startsWith('image/')) {
      setProfileError('avatar must be an image');
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setProfileError(
        `avatar too large (${String(Math.ceil(file.size / 1024))} KB, max ${String(
          Math.ceil(AVATAR_MAX_BYTES / 1024),
        )} KB)`,
      );
      return;
    }
    setProfileStatus('saving');
    try {
      // Avatars are semi-public (every peer in a chat sees them)
      // and need to be servable via a regular authenticated GET.
      // The dedicated /users/avatar route magic-byte-checks the
      // bytes (PNG / JPEG / GIF / WebP only) and stores the
      // canonical MIME alongside, so receivers render natively
      // without sniffing. 512 KB cap is enforced both client- and
      // server-side.
      const bytes = new Uint8Array(await file.arrayBuffer());
      const { mediaId } = await client.uploadAvatar(token, bytes);
      const next = await client.setMyProfile(token, { avatarMediaId: mediaId });
      setProfile(next);
      directory.seed(next);
      setProfileStatus('saved');
      setTimeout(() => {
        setProfileStatus('idle');
      }, 1500);
    } catch (err) {
      setProfileStatus('idle');
      setProfileError(err instanceof Error ? err.message : 'avatar upload failed');
    }
  }

  async function handleSaveProfile(): Promise<void> {
    setProfileError(undefined);
    setProfileStatus('saving');
    try {
      const updates: { username?: string | null; displayName?: string | null } = {};
      const currentUsername = profile?.username ?? '';
      const currentDisplayName = profile?.displayName ?? '';
      if (usernameDraft !== currentUsername) {
        updates.username = usernameDraft === '' ? null : usernameDraft;
      }
      if (displayNameDraft !== currentDisplayName) {
        updates.displayName = displayNameDraft === '' ? null : displayNameDraft;
      }
      if (Object.keys(updates).length === 0) {
        setProfileStatus('idle');
        setEditingProfile(false);
        return;
      }
      const next = await client.setMyProfile(token, updates);
      setProfile(next);
      directory.seed(next);
      setUsernameDraft(next.username ?? '');
      setDisplayNameDraft(next.displayName ?? '');
      setProfileStatus('saved');
      setEditingProfile(false);
      setTimeout(() => {
        setProfileStatus('idle');
      }, 1500);
    } catch (err) {
      setProfileStatus('idle');
      setProfileError(err instanceof Error ? err.message : 'save failed');
    }
  }

  async function handleStartConversationWith(userId: string): Promise<void> {
    if (!manager) return;
    let id;
    try {
      id = await manager.newConversation();
      await manager.invite(id, userId as UserId);
      directory.prefetch([userId]);
      onSelect(id);
      resetSearch();
    } catch (err) {
      // The group got created before the invite step failed — roll it
      // back so the user doesn't accumulate orphan "just me"
      // conversations from unsuccessful invite attempts.
      if (id) {
        try {
          await manager.removeConversation(id);
        } catch {
          /* best-effort cleanup */
        }
      }
      setInviteError(friendlyInviteError(err));
    }
  }

  // Force a re-render whenever the MemberDirectory cache changes so
  // conversation-list labels hydrate when profiles come in.
  useSyncExternalStore(
    useCallback((fn) => directory.subscribe(fn), [directory]),
    () => 1,
    () => 1,
  );

  const selfLabel = profile?.displayName ?? profile?.username ?? `user-${shortId(identity.userId)}`;

  return (
    <aside className="sidebar" aria-label="lobby">
      <div className="sidebar-section">
        <div className="profile-chip">
          {/* The avatar is itself the file-picker control — tapping it
              opens the photo chooser. The 📷 overlay + the
              `aria-label` hint expose the affordance without
              cluttering the chip. */}
          <label
            className="profile-avatar-edit"
            aria-label="change profile picture"
            title="Change profile picture"
          >
            {(() => {
              const url = directory.avatarUrl(identity.userId);
              return url ? (
                <img className="avatar lg" src={url} alt={selfLabel} data-testid="profile-avatar" />
              ) : (
                <span className="avatar lg" aria-hidden="true">
                  {initialsFor(selfLabel)}
                </span>
              );
            })()}
            <span className="profile-avatar-edit-hint" aria-hidden="true">
              📷
            </span>
            <input
              data-testid="profile-avatar-input"
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleAvatarFile(file);
                e.target.value = '';
              }}
            />
          </label>
          <div className="names">
            <span className="name">{selfLabel}</span>
            <span className="handle">
              {profile?.username ? `@${profile.username}` : `user-${shortId(identity.userId)}`}
            </span>
          </div>
          <button
            className="icon"
            type="button"
            aria-label="edit profile"
            title="Edit profile"
            onClick={() => {
              setEditingProfile((v) => !v);
            }}
          >
            ✎
          </button>
        </div>
        {editingProfile && (
          <form
            className="vstack"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSaveProfile();
            }}
          >
            <label>
              display name
              <input
                data-testid="profile-displayname-input"
                value={displayNameDraft}
                onChange={(e) => {
                  setDisplayNameDraft(e.target.value);
                }}
                placeholder="how others see you"
              />
            </label>
            <label>
              username
              <input
                data-testid="profile-username-input"
                value={usernameDraft}
                onChange={(e) => {
                  setUsernameDraft(e.target.value);
                }}
                placeholder="3–20 lowercase letters, digits, _-"
                autoComplete="off"
                spellCheck={false}
              />
            </label>
            <div className="hstack">
              <button
                data-testid="profile-save"
                className="primary"
                type="submit"
                disabled={profileStatus === 'saving'}
              >
                {profileStatus === 'saving' ? 'saving…' : 'save'}
              </button>
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  setEditingProfile(false);
                }}
              >
                cancel
              </button>
              {profileStatus === 'saved' && (
                <span data-testid="profile-saved" className="save-status">
                  ✓ saved
                </span>
              )}
            </div>
            {profileError && (
              <p data-testid="profile-error" style={{ color: 'var(--danger)' }}>
                {profileError}
              </p>
            )}
            {onExportBackup && (
              <div className="backup-actions">
                <p className="faint" style={{ fontSize: '0.85em', marginBottom: '0.3em' }}>
                  Download a passphrase-sealed backup of your identity + chats. Anyone with this
                  file <strong>and</strong> your passphrase can read your conversations — keep it
                  somewhere safe.
                </p>
                <button
                  type="button"
                  className="ghost"
                  data-testid="backup-download"
                  onClick={() => {
                    void (async () => {
                      try {
                        const bundle = await onExportBackup();
                        const blob = new Blob([JSON.stringify(bundle)], {
                          type: 'application/json',
                        });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        const stamp = new Date().toISOString().slice(0, 10);
                        a.download = `gamez-backup-${stamp}.json`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      } catch (err) {
                        setProfileError(err instanceof Error ? err.message : 'backup failed');
                      }
                    })();
                  }}
                >
                  download backup
                </button>
              </div>
            )}
          </form>
        )}
      </div>

      {manager && manager.blocked().length > 0 && (
        <div className="sidebar-section">
          <h3>Blocked</h3>
          <ul className="blocked-list" data-testid="blocked-list">
            {manager.blocked().map((uid) => (
              <li key={uid} className="blocked-row">
                <span className="avatar sm" aria-hidden="true">
                  {initialsFor(directory.label(uid))}
                </span>
                <span className="truncate">{directory.label(uid)}</span>
                <button
                  type="button"
                  className="ghost"
                  onClick={() => {
                    void manager.unblock(uid);
                  }}
                >
                  unblock
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="sidebar-section">
        <h3>Find users</h3>
        <div className="search-input">
          <input
            data-testid="user-search-input"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            placeholder="username or user- prefix"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        {(searchError ?? inviteError) && (
          <p data-testid="search-error" style={{ color: 'var(--danger)' }}>
            {searchError ?? inviteError}
          </p>
        )}
        {results.length > 0 && (
          <ul className="search-results" data-testid="search-results">
            {results.map((r) => {
              const label = r.displayName ?? r.username ?? `user-${shortId(r.userId)}`;
              return (
                <li key={r.userId}>
                  <button
                    type="button"
                    className="use-btn"
                    data-testid={`search-pick-${r.userId}`}
                    onClick={() => void handleStartConversationWith(r.userId)}
                    title="Start a conversation"
                  >
                    <span className="avatar sm" aria-hidden="true">
                      {initialsFor(label)}
                    </span>
                    <span className="grow truncate">
                      <strong>{label}</strong>
                      {r.username && r.displayName && <span className="muted"> @{r.username}</span>}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {searchQuery.trim().length >= 2 && results.length === 0 && !searchError && (
          <p data-testid="search-empty" className="empty-note">
            no matches
          </p>
        )}
      </div>

      <div className="sidebar-section">
        <h3>Search messages</h3>
        <div className="search-input">
          <input
            data-testid="message-search-input"
            value={msgQuery}
            onChange={(e) => {
              setMsgQuery(e.target.value);
            }}
            placeholder="type to search your history"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        {msgQuery.trim().length >= 2 && msgResults.length > 0 && (
          <ul className="search-results" data-testid="message-search-results">
            {msgResults.map((hit) => {
              const meta = manager?.meta(hit.conversationId);
              const members = manager?.members(hit.conversationId) ?? [];
              const peers = members.filter((m) => m !== identity.userId);
              const convName =
                meta?.name ??
                (peers.length === 0
                  ? 'Empty chat'
                  : peers.map((p) => directory.label(p)).join(', '));
              return (
                <li key={`${hit.conversationId}:${String(hit.sequence)}`}>
                  <button
                    type="button"
                    className="use-btn"
                    data-testid={`message-hit-${String(hit.sequence)}`}
                    onClick={() => {
                      if (onSelectMessage) onSelectMessage(hit.conversationId, hit.sequence);
                      else onSelect(hit.conversationId);
                      setMsgQuery('');
                      setMsgResults([]);
                    }}
                  >
                    <span className="avatar sm" aria-hidden="true">
                      {initialsFor(convName)}
                    </span>
                    <span className="grow truncate">
                      <strong>{convName}</strong>
                      <span className="muted"> · {hit.snippet}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {msgQuery.trim().length >= 2 && !msgSearching && msgResults.length === 0 && (
          <p data-testid="message-search-empty" className="empty-note">
            no matches
          </p>
        )}
      </div>

      <div className="sidebar-section">
        <div className="hstack">
          <button
            data-testid="create-conversation"
            className="primary"
            type="button"
            onClick={onCreate}
          >
            + new chat
          </button>
          <button
            data-testid="find-opponent"
            className="ghost"
            type="button"
            disabled={matchmakingStatus !== 'idle'}
            onClick={onFindOpponent}
            title="Quick-match tic-tac-toe"
          >
            {matchmakingStatus === 'searching'
              ? '⏳ searching'
              : matchmakingStatus === 'waiting'
                ? '⏳ waiting'
                : '⭕ find match'}
          </button>
        </div>
        <button
          data-testid="enable-notifications"
          className="ghost"
          type="button"
          onClick={onEnableNotifications}
        >
          🔔 enable notifications
          {pushStatus !== 'idle' && (
            <span data-testid="push-status" className="muted">
              {' '}
              · {pushStatus}
            </span>
          )}
        </button>
      </div>

      <div className="sidebar-section" style={{ flex: 0, paddingBottom: '0.4em' }}>
        <h3>Conversations</h3>
      </div>
      <div className="sidebar-scroll">
        {conversations.length === 0 ? (
          <p className="empty-note" style={{ padding: '0.6em 1em' }}>
            No conversations yet. Click "+ new chat" or find a user above.
          </p>
        ) : (
          <ul className="conversation-list">
            {[...conversations]
              .sort((a, b) => {
                const ma = manager?.meta(a);
                const mb = manager?.meta(b);
                // Pinned conversations stay at the top regardless of
                // activity. Within each group sort by latestSequence
                // (server-assigned, monotonic) and fall back to
                // updatedAt for conversations that haven't received
                // any messages yet.
                const pa = ma?.pinned ? 1 : 0;
                const pb = mb?.pinned ? 1 : 0;
                if (pa !== pb) return pb - pa;
                const ka = ma?.latestSequence ?? ma?.updatedAt ?? 0;
                const kb = mb?.latestSequence ?? mb?.updatedAt ?? 0;
                return kb - ka;
              })
              .map((id) => (
                <ConversationRow
                  key={id}
                  conversationId={id}
                  selected={id === selected}
                  self={identity.userId}
                  manager={manager}
                  directory={directory}
                  onClick={() => onSelect(id)}
                  onDelete={() => onDeleteConversation(id)}
                />
              ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

function friendlyInviteError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg.includes('no key package available')) {
    return "that user isn't reachable right now — ask them to open the app so their device can publish new keys";
  }
  return msg || 'invite failed';
}

interface ConversationRowProps {
  readonly conversationId: ConversationId;
  readonly selected: boolean;
  readonly self: string;
  readonly manager: ConversationManager | undefined;
  readonly directory: MemberDirectory;
  readonly onClick: () => void;
  readonly onDelete: () => void;
}

function ConversationRow({
  conversationId,
  selected,
  self,
  manager,
  directory,
  onClick,
  onDelete,
}: ConversationRowProps) {
  const meta = manager?.meta(conversationId);
  const members = manager?.members(conversationId) ?? [];
  const peers = members.filter((m) => m !== self);
  const derivedName =
    peers.length === 0 ? 'Empty chat' : peers.map((m) => directory.label(m)).join(', ');
  const name = meta?.name ?? derivedName;
  const subtitle =
    peers.length === 0
      ? 'just you — invite someone'
      : peers.length === 1
        ? `with ${directory.label(peers[0]!)}`
        : `${String(peers.length)} members`;
  const unread = manager?.unreadCount(conversationId) ?? 0;

  return (
    <li className="conversation-row-wrap">
      <button
        type="button"
        className={`conversation-item${selected ? ' selected' : ''}${
          unread > 0 ? ' has-unread' : ''
        }`}
        data-testid={`select-conv-${conversationId}`}
        onClick={onClick}
      >
        <span className="avatar md" aria-hidden="true">
          {initialsFor(name)}
        </span>
        <div className="conv-body">
          <span className="conv-title">
            {name}
            {meta?.muted ? (
              <span
                className="conv-muted"
                aria-label="muted"
                title="Muted"
                data-testid={`muted-${conversationId}`}
              >
                {' 🔕'}
              </span>
            ) : null}
          </span>
          <span className="conv-sub">{subtitle}</span>
        </div>
        {unread > 0 && (
          <span
            className="unread-badge"
            data-testid={`unread-${conversationId}`}
            aria-label={`${String(unread)} unread message${unread === 1 ? '' : 's'}`}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      <button
        type="button"
        className="icon conversation-row-pin"
        aria-label={meta?.pinned ? `unpin ${name}` : `pin ${name}`}
        title={meta?.pinned ? 'Unpin' : 'Pin to top'}
        data-testid={`pin-${conversationId}`}
        onClick={(e) => {
          e.stopPropagation();
          if (manager) void manager.setPinned(conversationId, !meta?.pinned);
        }}
      >
        {meta?.pinned ? '📌' : '📍'}
      </button>
      <button
        type="button"
        className="icon conversation-row-delete"
        aria-label={`delete ${name}`}
        title="Remove from device"
        onClick={(e) => {
          e.stopPropagation();
          if (
            window.confirm(
              `Remove "${name}" from this device? Server-side messages stay put but will be unreachable.`,
            )
          ) {
            onDelete();
          }
        }}
      >
        🗑
      </button>
    </li>
  );
}
